import * as Sentry from '@sentry/react';
import mixpanel from 'mixpanel-browser';

import { CustomUser } from 'generated-api';
import { toaster } from 'ui/Toaster';

// Initialize Sentry if the DSN is available
if (window.env.SENTRY_DSN_FRONTEND) {
  Sentry.init({
    dsn: window.env.SENTRY_DSN_FRONTEND,
    integrations: [
      new Sentry.BrowserTracing(),
      new Sentry.Replay({
        // Don't hide any screen information in the recording
        blockAllMedia: false,
        maskAllInputs: false,
        maskAllText: false,
        // Also allow all domain request data to be recorded
        networkDetailAllowUrls: [window.location.origin],
      }),
    ],
    // Preserve deep structure of errors
    normalizeDepth: 8,
    // Capture only 10% of total transactions for performance monitoring
    tracesSampleRate: 0.1,
    // Disable session replays by default but record all sessions with errors for later replays
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,
    // Deny errors from certain URLs to reduce noise
    // See https://docs.sentry.io/platforms/javascript/configuration/filtering/#decluttering-sentry
    denyUrls: [
      // Google Ads tracking
      /pagead\/viewthroughconversion/i,
    ],
  });
}

// Initialize Mixpanel if the project token is available
// TODO: Add page_view tracking and consider autocapture
// See https://docs.mixpanel.com/docs/quickstart/capture-events/autocapture
if (window.env.MIXPANEL_TOKEN) {
  mixpanel.init(window.env.MIXPANEL_TOKEN, {
    persistence: 'localStorage',
  });
}

// Export the analytics wrapper for use throughout the app
export const analytics = {
  reportError: (
    message: string,
    extra: Record<string, any>,
    showToaster = true
  ) => {
    const error = new Error(message);
    // Report error to the Sentry service if available
    if (window.env.SENTRY_DSN_FRONTEND) {
      Sentry.captureException(error, { extra });
    } else {
      console.warn(`[Sentry Capture] ${message}`, extra, error);
    }
    // And also display error to the user
    if (showToaster) {
      toaster.error({ title: message });
    }
  },
  track: (eventName: string, properties?: Record<string, any>) => {
    if (window.env.MIXPANEL_TOKEN) {
      mixpanel.track(eventName, properties);
    } else {
      console.debug(`[Mixpanel Track] ${eventName}`, properties);
    }
  },
  identify: (user: CustomUser) => {
    if (window.env.MIXPANEL_TOKEN) {
      mixpanel.identify(user.email);
      mixpanel.people.set({
        $name: `${user.first_name} ${user.last_name}`,
        $email: user.email,
        isCustomer: user.has_active_subscription,
        isStaff: user.is_staff,
      });
    }
    if (window.env.SENTRY_DSN_FRONTEND) {
      Sentry.setUser({ email: user.email });
    }
    console.debug(`[Analytics Identify] ${user.email}`);
  },
  reset: () => {
    if (window.env.MIXPANEL_TOKEN) {
      mixpanel.reset();
    }
    if (window.env.SENTRY_DSN_FRONTEND) {
      Sentry.setUser(null);
    }
    console.debug('[Analytics Reset]');
  },
};
